import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tab } from '@headlessui/react';
import {
  FiPlus, FiCalendar, FiClock, FiEdit3, FiArchive, FiEye,
  FiFilter, FiRefreshCw, FiAlertTriangle, FiUser, FiBriefcase,
} from 'react-icons/fi';
import { fetchPosts, fetchStats, approvePost, cancelPost, retryPost } from '../store/thunks/socialPostsActions';
import { setCurrentPost, clearCurrentPost, setFilters, clearFilters } from '../store/slices/socialPostsSlice';
import { POST_STATUSES } from '../services/SocialPostService';
import PostTimeline from '../components/socialComponents/PostTimeline';
import PostQueueList from '../components/socialComponents/PostQueueList';
import PostDraftsList from '../components/socialComponents/PostDraftsList';
import PostApprovalList from '../components/socialComponents/PostApprovalList';
import PostHistoryList from '../components/socialComponents/PostHistoryList';
import PostDetailDrawer from '../components/socialComponents/PostDetailDrawer';
import PostFilters from '../components/socialComponents/PostFilters';

function StatChip({ icon: Icon, label, count, color = 'zinc', pulse = false }) {
  const colors = {
    zinc: 'bg-zinc-50 text-zinc-600 ring-zinc-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    indigo: 'bg-indigo-50 text-indigo-600 ring-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    red: 'bg-red-50 text-red-600 ring-red-200',
    violet: 'bg-violet-50 text-violet-600 ring-violet-200',
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl ring-1 ${colors[color] || colors.zinc}`}>
      <Icon className="w-4 h-4" />
      <span className="text-xs font-medium">{label}</span>
      <span className={`text-sm font-bold tabular-nums ${pulse ? 'animate-pulse' : ''}`}>{count ?? '—'}</span>
    </div>
  );
}

export default function SocialPublishingScreen() {
  const dispatch = useDispatch();
  const { posts, stats, loading, filters, currentPost } = useSelector((s) => s.socialPosts);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPost, setDrawerPost] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [accountFilter, setAccountFilter] = useState(null);

  const loadPosts = useCallback(() => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.platform) params.platform = filters.platform;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.sort) params.sort = filters.sort;
    if (accountFilter) params.account = accountFilter;
    dispatch(fetchPosts(params));
    dispatch(fetchStats());
  }, [dispatch, filters, accountFilter]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const counts = useMemo(() => {
    if (stats) return stats;
    const c = { draft: 0, pending_approval: 0, approved: 0, queued: 0, posted: 0, failed: 0, canceled: 0 };
    posts.forEach((p) => { if (c[p.status] !== undefined) c[p.status]++; });
    return c;
  }, [stats, posts]);

  const openNewPost = useCallback(() => { setDrawerPost(null); setDrawerOpen(true); }, []);
  const openPost = useCallback((post) => { setDrawerPost(post); dispatch(setCurrentPost(post)); setDrawerOpen(true); }, [dispatch]);
  const handleEdit = useCallback((postId) => { const p = posts.find((x) => x._id === postId); if (p) openPost(p); }, [posts, openPost]);
  const handleApprove = useCallback(async (postId) => { await dispatch(approvePost(postId)); loadPosts(); }, [dispatch, loadPosts]);
  const handleCancel = useCallback(async (postId) => { await dispatch(cancelPost(postId)); loadPosts(); }, [dispatch, loadPosts]);
  const handleRetry = useCallback(async (postId) => { await dispatch(retryPost(postId)); loadPosts(); }, [dispatch, loadPosts]);
  const handleQueue = useCallback((postId) => { const p = posts.find((x) => x._id === postId); if (p) openPost(p); }, [posts, openPost]);
  const handleDrawerClose = useCallback(() => { setDrawerOpen(false); setDrawerPost(null); dispatch(clearCurrentPost()); }, [dispatch]);
  const handleDrawerSaved = useCallback(() => { loadPosts(); }, [loadPosts]);
  const handleFilterChange = useCallback((f) => { dispatch(setFilters(f)); }, [dispatch]);
  const handleFilterClear = useCallback(() => { dispatch(clearFilters()); setAccountFilter(null); }, [dispatch]);

  const tabItems = [
    { icon: FiEdit3, label: 'Drafts', count: counts.draft || 0 },
    { icon: FiEye, label: 'Needs Approval', count: (counts.pending_approval || 0) + (counts.failed || 0) },
    { icon: FiClock, label: 'Queue', count: (counts.approved || 0) + (counts.queued || 0) },
    { icon: FiCalendar, label: 'Timeline', count: null },
    { icon: FiArchive, label: 'History', count: (counts.posted || 0) + (counts.canceled || 0) },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-6 lg:px-8 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Social Publishing</h1>
              <p className="text-sm text-zinc-500 mt-1">Manage, schedule, and publish social media content</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={loadPosts} disabled={loading} className="p-2.5 rounded-xl text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors disabled:opacity-50" title="Refresh">
                <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${showFilters ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}>
                <FiFilter className="w-4 h-4" /> Filters
              </button>
              <button onClick={openNewPost} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow-md transition-all">
                <FiPlus className="w-4 h-4" /> New Post
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 flex-wrap">
            <StatChip icon={FiEdit3} label="Drafts" count={counts.draft} color="zinc" />
            <StatChip icon={FiAlertTriangle} label="Needs Approval" count={counts.pending_approval} color="amber" pulse={counts.pending_approval > 0} />
            <StatChip icon={FiClock} label="Queued" count={(counts.approved || 0) + (counts.queued || 0)} color="indigo" />
            <StatChip icon={FiArchive} label="Posted" count={counts.posted} color="emerald" />
            {counts.failed > 0 && <StatChip icon={FiAlertTriangle} label="Failed" count={counts.failed} color="red" pulse />}

            {/* Account quick filter */}
            <div className="ml-auto flex items-center gap-1.5">
              <span className="text-xs text-zinc-400 mr-1">Account:</span>
              {[
                { key: null, label: 'All', icon: null },
                { key: 'personal', label: 'Personal', icon: FiUser },
                { key: 'business', label: 'Business', icon: FiBriefcase },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={label}
                  onClick={() => setAccountFilter(key)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    accountFilter === key
                      ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200'
                      : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'
                  }`}
                >
                  {Icon && <Icon className="w-3 h-3" />}
                  {label}
                </button>
              ))}
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <PostFilters filters={filters} onChange={handleFilterChange} onClear={handleFilterClear} />
            </div>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-8">
          <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
            <Tab.List className="flex gap-1 -mb-px">
              {tabItems.map(({ icon: Icon, label, count }, idx) => (
                <Tab key={label} className={({ selected }) => `
                  flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all outline-none
                  ${selected ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'}
                `}>
                  <Icon className="w-4 h-4" />
                  {label}
                  {count != null && count > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${selectedTab === idx ? 'bg-indigo-100 text-indigo-700' : 'bg-zinc-100 text-zinc-500'}`}>
                      {count}
                    </span>
                  )}
                </Tab>
              ))}
            </Tab.List>

            <Tab.Panels className="px-6 lg:px-8 py-6 max-w-[1600px] mx-auto">
              {/* Drafts */}
              <Tab.Panel className="focus:outline-none">
                <PostDraftsList posts={posts} loading={loading} onEdit={handleEdit} onApprove={handleApprove} onQueue={handleQueue} onCancel={handleCancel} onRetry={handleRetry} onClick={openPost} />
              </Tab.Panel>

              {/* Needs Approval */}
              <Tab.Panel className="focus:outline-none">
                <PostApprovalList posts={posts} loading={loading} onEdit={handleEdit} onApprove={handleApprove} onQueue={handleQueue} onCancel={handleCancel} onRetry={handleRetry} onClick={openPost} />
              </Tab.Panel>

              {/* Queue */}
              <Tab.Panel className="focus:outline-none">
                <PostQueueList posts={posts} loading={loading} onEdit={handleEdit} onApprove={handleApprove} onQueue={handleQueue} onCancel={handleCancel} onRetry={handleRetry} onClick={openPost} />
              </Tab.Panel>

              {/* Timeline */}
              <Tab.Panel className="focus:outline-none">
                <PostTimeline posts={posts} loading={loading} onEdit={handleEdit} onApprove={handleApprove} onQueue={handleQueue} onCancel={handleCancel} onRetry={handleRetry} onClick={openPost} />
              </Tab.Panel>

              {/* History */}
              <Tab.Panel className="focus:outline-none">
                <PostHistoryList posts={posts} loading={loading} onClick={openPost} />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>

      <PostDetailDrawer post={drawerPost || currentPost} open={drawerOpen} onClose={handleDrawerClose} onSaved={handleDrawerSaved} />
    </div>
  );
}
